import Vue from "vue";
import apiSkobo from "@/helpers/api/apiSkobo";
import ConfidentialFolder from "@/models/People.js/ConfidentialFolder";
import { usePeopleStore } from "@/stores/people";
import { useSettingsStore } from "@/stores/settings";

export const state_confidentialdocuments = {
  confidentialDocumentsObs: [],
  sectionConfidentialFoldersMap: {},
  specialItemTypesMap: {},
  specialItemsForAppConfidentialMap: {} // { driveId: { itemId: specialItemType } }
};

export const actions_confidentialdocuments = {
  async loadConfidentialFoldersForSection(sectionId) {
    if (this.sectionConfidentialFoldersMap[sectionId]) {
      return;
    }
    await this.reloadConfidentialFoldersForSection(sectionId);
  },

  async reloadConfidentialFoldersForSection(sectionId) {
    const peopleStore = usePeopleStore();
    const promises = [];
    promises.push(await apiSkobo.get(`/appconfidentialfolder/section/${sectionId}`));
    promises.push(await peopleStore.loadAllPeopleOnInstitution());

    const response = await Promise.all(promises);

    const confidentialFolders = response[0].data.map(cf => {
      const confidentialFolder = new ConfidentialFolder(cf);
      if (!confidentialFolder.extSysApiUserId && confidentialFolder.userId) {
        const person = peopleStore.fromId(confidentialFolder.userId);
        confidentialFolder.extSysApiUserId = person ? person.extSysApiUserId : null;
      }
      return confidentialFolder;
    });

    Vue.set(this.sectionConfidentialFoldersMap, sectionId, confidentialFolders);
    //this.sectionConfidentialFoldersMap[sectionId] = confidentialFolders;
  },

  async loadConfidentialDocumentsObs() {
    if (this.confidentialDocumentsObs.length === 0) {
      await this.reloadConfidentialDocumentsObs();
    }
  },

  async reloadConfidentialDocumentsObs() {
    const res = await apiSkobo.get("/appconfidentialfolder/obs/list");
    this.confidentialDocumentsObs = res.data.map(confidentialFolder => new ConfidentialFolder(confidentialFolder));
  },

  async grantAccessToConfidentialDocumentsObs(confidentialId, sectionId) {
    try {
      await apiSkobo.post(`/appconfidentialfolder/${confidentialId}/grant`, {
        sectionId
      });
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Verify a users confidential documents to let leader know they are ready to archive
   * Used in teacher view (hence sectionId parm)
   * @param { string } confidentialFolderId guid of confidential folder to verify
   * @param { string} sectionId sectionId of the current user
   */
  async verifyConfidentialFolder(confidentialFolderId, sectionId) {
    await apiSkobo.post(`/appconfidentialfolder/obs/${confidentialFolderId}/verified`);
    await this.reloadConfidentialFoldersForSection(sectionId);
  },

  /**
   * Archive a persons confidential documents - with a target back-end will move them to target.
   * Used in administration view
   * @param { string } confidentialFolderId guid of confidential folder to archive
   * @param { string } targetId guid of target to move documents to on archive
   */
  async archiveConfidentialFolder(confidentialFolderId, targetId) {
    let body;
    if (targetId) {
      body = { targetId };
    }
    await apiSkobo.post(`/appconfidentialfolder/obs/${confidentialFolderId}/archive`, body);
    await this.reloadConfidentialDocumentsObs();
  },

  /**
   * Adds an item type to the driveItem
   * @param { string } confidentialId Confidential id the item is connected to
   * @param { string } driveId Drive id of the item
   * @param { string } itemId Item id of the item
   * @param { string } specialItemType Item type of the item
   * @param { boolean } persist Indicates whether the special item should be persisted to database or only exist in state
   */
  async addSpecialItem(confidentialId, driveId, itemId, specialItemType, persist = true) {
    {
      const specialItem = {
        confidentialId,
        driveId,
        itemId,
        itemType: specialItemType
      };

      let response;

      if (persist) {
        response = await apiSkobo.post("/appconfidentialfolder/specialitems", specialItem);
      }

      if ((response && response.status == 200) || !persist) {
        const clone = this.specialItemTypesMap;

        if (!clone[driveId]) {
          clone[driveId] = {};
        }

        clone[driveId][itemId] = specialItemType;

        this.specialItemTypesMap = clone;
        this.specialItemsForAppConfidentialMap[confidentialId] = true;
      }
    }
  },

  /**
   *
   * @param { string } appConfidentialId AppConfidential id to load special items for
   */
  async loadSpecialItems(appConfidentialId) {
    if (!this.specialItemsForAppConfidentialMap[appConfidentialId]) {
      await this.reloadSpecialItems(appConfidentialId);
    }
  },

  /**
   * Loads special items for a specific appConfidentialId
   * @param { string } appConfidentialId AppConfidential id to load special items for
   */
  async reloadSpecialItems(appConfidentialId) {
    const response = await apiSkobo.get(`/appconfidentialfolder/specialitems/${appConfidentialId}`);

    if (response.status === 200) {
      const clone = this.specialItemTypesMap;
      response.data.forEach(item => {
        if (!clone[item.driveId]) {
          clone[item.driveId] = {};
        }
        clone[item.driveId][item.itemId] = item.itemType;
      });

      this.specialItemTypesMap = clone;
      this.specialItemsForAppConfidentialMap[appConfidentialId] = true;
    }
  },

  /**
   * Calculate special item type for drive item
   * @param { object} driveItem DriveItem to calculate type for
   * @returns { string } Special item type
   */
  async calculateSpecialItemType(driveItem) {
    const testSpecialItem = this.specialItemTypeForDriveItem(driveItem.parentReference.driveId, driveItem.id);
    if (testSpecialItem) {
      return testSpecialItem;
    }

    if (driveItem.name === "root") {
      return undefined;
    }

    const hierarchyAbove = await this.calculateDriveItemsHierarchy(driveItem.parentReference.driveId, driveItem.id);
    let specialItemTypeForParent;
    const clone = Object.assign({}, this.specialItemTypesMap);

    for (let i = 0; i < hierarchyAbove.length; i++) {
      if (specialItemTypeForParent) {
        if (!clone[driveItem.parentReference.driveId]) {
          clone[driveItem.parentReference.driveId] = {};
        }
        clone[driveItem.parentReference.driveId][driveItem.id] = specialItemTypeForParent;
      } else {
        specialItemTypeForParent = this.specialItemTypeForDriveItem(
          hierarchyAbove[i].driveId,
          hierarchyAbove[i].itemId
        );
      }
    }

    Vue.set(this, "specialItemTypesMap", clone);
    return specialItemTypeForParent;
  }
};

export const getters_confidentialdocuments = {
  confidentialFolderForPeopleInSection: state => sectionId => {
    return state.sectionConfidentialFoldersMap[sectionId]?.filter(cf => !!cf.userId) || [];
  },

  confidentialFolderForSectionInSection: state => sectionId => {
    return state.sectionConfidentialFoldersMap[sectionId]?.filter(cf => !!cf.sectionId) || [];
  },

  confidentialFoldersForPersonInSection: state => (sectionId, userId) => {
    if (state.sectionConfidentialFoldersMap[sectionId]) {
      return state.sectionConfidentialFoldersMap[sectionId].find(cf => cf.userId === userId);
    }
    return null;
  },

  confidentialFolderFromId: state => (driveId, itemId) => {
    for (let sect in state.sectionConfidentialFoldersMap) {
      const userFolder = state.sectionConfidentialFoldersMap[sect].find(
        cf => cf.parentReference && cf.parentReference.driveId === driveId && cf.id === itemId
      );

      if (userFolder) {
        return userFolder;
      }
    }

    const confidentialDocumentsRootsFromObs = state.confidentialDocumentsObs.find(
      cf => cf.parentReference && cf.parentReference.driveId === driveId && cf.id === itemId
    );
    if (confidentialDocumentsRootsFromObs) {
      return confidentialDocumentsRootsFromObs;
    }

    return null;
  },

  isDriveConfidentialFolder: state => driveId => {
    for (let sect in state.sectionConfidentialFoldersMap) {
      return state.sectionConfidentialFoldersMap[sect].some(cf => cf.driveId && cf.driveId === driveId);
    }
    return false;
  },

  confidentialDocumentsObsSections(state) {
    const settingsStore = useSettingsStore();

    const sectionArray = state.confidentialDocumentsObs.map(obs => obs.sectionDisplayName);
    return [...new Set(sectionArray)].sort((a, b) => a.localeCompare(b, settingsStore.language));
  },

  confidentialDocumentsObsForSection(state) {
    return sectionDisplayName => {
      return state.confidentialDocumentsObs.filter(obs => obs.sectionDisplayName === sectionDisplayName);
    };
  },

  confidentialDocumentsObsForPerson(state) {
    return personId => {
      return state.confidentialDocumentsObs.find(obs => obs.userId === personId);
    };
  },

  specialItemTypeForDriveItem: state => (driveId, itemId) => {
    const settingsStore = useSettingsStore();

    return state.specialItemTypesMap[driveId]
      ? state.specialItemTypesMap[driveId][itemId]
      : settingsStore.templateSpecialItems.find(spi => spi.driveId === driveId && spi.itemId === itemId)
          ?.specialItemType;
  },

  specialItemObjectForDriveItem: state => (driveId, itemId) => {
    const settingsStore = useSettingsStore();
    const specialItemType = state.specialItemTypeForDriveItem(driveId, itemId);

    if (!specialItemType) {
      return null;
    }

    return settingsStore.templateSpecialItems.find(spi => spi.specialItemType == specialItemType);
  }
};
